import { Box, Flex, Heading, Text } from "@radix-ui/themes";
import { AnimatedAccordion } from "~/components/AnimatedAccordion";
import { HeroStyle2 } from "~/components/HeroStyle2";
import { HomeConsultForm } from "~/components/home/HomeConsultForm";
import {
	Locale,
	useLocalization,
} from "~/components/localization/LocalizationProvider";
import styles from "../live-seo-page.module.css";

type EmergencyItem = {
	title: string;
	paragraphs: string[];
};

type OrthoEmergenciesLocalization = {
	title: string;
	introHeading: string;
	introParagraphs: string[];
	helpHeading: string;
	helpParagraphs: string[];
	items: EmergencyItem[];
};

const englishItems: EmergencyItem[] = [
	{
		title: "Discomfort",
		paragraphs: [
			"It’s normal for you to have discomfort for a day or two after braces or retainers are adjusted, but it can make eating uncomfortable.",
			"This discomfort is very normal and only for a short time.",
			"Try eating soft foods and rinse the mouth with warm salt water.",
		],
	},
	{
		title: "Mouth Sores",
		paragraphs: [
			"You may be susceptible to mouth sores. While your braces don’t cause them, they may be made worse by irritation from braces.",
			"One or several areas patches of sores may appear on the cheeks, lips or tongue. This is not an emergency, but may be very uncomfortable for you.",
			"Get quick relief by applying a small amount of topical anesthetic (such as Orabase or Ora-Gel) directly to the area with sores using a cotton swab.",
			"You can reapply as needed.",
		],
	},
	{
		title: "Food Caught Between Your Teeth",
		paragraphs: [
			"This is not an emergency, but it can be uncomfortable or embarrassing. It’s easily fixed with a piece of dental floss.",
			"Try tying a small knot in the middle of the floss to help remove the food, or use an interproximal brush or toothpick to dislodge food caught between your teeth and your braces.",
		],
	},
	{
		title: "Irritated Lips or Cheeks",
		paragraphs: [
			"Sometimes new braces can irritate your mouth, especially when you're eating. A small amount of non-medicinal relief wax makes an excellent buffer between the metal and your mouth.",
			"Simply pinch off a small piece and roll it into a ball the size of a small pea. Flatten the ball and place it completely over the area of the braces causing irritation.",
			"Then, you can eat more comfortably. If you accidentally swallow the wax, it’s not a problem. The wax is harmless.",
		],
	},
	{
		title: "Ligatures Coming Off",
		paragraphs: [
			"Tiny rubber bands or small, fine wires, known as ligatures, hold the wire to the bracket. If a rubber ligature should come off, you may be able to put it back in place using sterile tweezers. If a wire ligature comes loose, simply remove it with sterile tweezers.",
			"If your wire ligature is sticking out into the lip but is not loose, you can bend it back down with a Q-tip or pencil eraser so it doesn’t irritate your lip. Of course, when one ligature pops off or breaks, others may follow.",
			"Be sure to examine all ligatures. Missing or broken ligatures should be brought to the attention of Dr. Garcia-Paul. If a rubber or wire ligature is lost, tell Dr. Garcia-Paul so she can advise whether you should be seen.",
		],
	},
	{
		title: "Protruding Wire",
		paragraphs: [
			"Occasionally, the end of a wire will work itself out of place and irritate your mouth. Use a Q-tip or pencil eraser to push the wire so that it is flat against the tooth.",
			"If the wire cannot be moved into a comfortable position, cover it with relief wax. (See Irritated of Lips or Cheeks above for instructions on applying relief wax.)",
			"You'll need to make Dr. Garcia-Paul aware of the problem.",
			"In a situation where the wire is extremely bothersome and you can't see Dr. Garcia-Paul anytime soon, you may, as a last resort, clip the wire.",
			"Reduce the possibility swallowing the snipped piece of wire by using folded tissue or gauze around the area.",
			"Use a pair of sharp clippers and snip off the protruding wire. Relief wax may still be necessary to provide comfort to the irritated area.",
		],
	},
	{
		title: "Loose Brackets, Wires, or Bands",
		paragraphs: [
			"If your braces come loose in any way, call Dr. Garcia-Paul to determine the next steps.",
			"Brackets are the parts of braces attached to teeth with a special adhesive. They are generally positioned in the center of each tooth. The bracket can be knocked off if you've eaten one of those hard or crunchy foods you are instructed to avoid, or if the mouth is struck while at play.",
			"We encourage all patients, especially those with braces, to wear a protective mouth guard while playing sports.",
			"If the bracket is off center, the adhesive may have failed. In this instance, it is best to immediately notify Dr. Garcia-Paul, who will determine the course of action.",
			"If the loose bracket has rotated on the wire and is sticking out and you can't come to our office immediately, you can do a temporary fix to alleviate discomfort and prevent further damage, but take care to prevent swallowing or another injury.",
			"To put the bracket back in place, use sterile tweezers to slide the bracket along the wire until it is between two teeth.",
		],
	},
	{
		title: "You Swallow a Piece of Your Appliance",
		paragraphs: [
			"This is rare, but when it does happen, it can be alarming but stay calm.",
			"If you're coughing excessively or having difficulty breathing, you may have inhaled the piece into your airways or lungs.",
			"If you are able to see the piece, you may carefully attempt to remove it.",
			"DO NOT make the attempt if you could cause harm.",
			"If appropriate under the circumstances, examine your braces for problems that may result from the missing piece, such as looseness or irritation, and treat as specified above.",
			"If you are unable to see the piece and believe it may be have been inhaled, call Dr. Garcia-Paul immediately.",
		],
	},
];

const spanishItems: EmergencyItem[] = [
	{
		title: "Molestias",
		paragraphs: [
			"Es normal tener molestias durante uno o dos días después de ajustar los brackets o retenedores, pero puede hacer que comer sea incómodo.",
			"Esta molestia es muy normal y dura poco tiempo.",
			"Prueba comer alimentos blandos y enjuagarte la boca con agua tibia con sal.",
		],
	},
	{
		title: "Llagas en la boca",
		paragraphs: [
			"Puedes ser propenso a tener llagas en la boca. Aunque los brackets no las causan, pueden empeorarlas por la irritación.",
			"Pueden aparecer una o varias zonas con llagas en las mejillas, los labios o la lengua. Esto no es una emergencia, pero puede ser muy incómodo.",
			"Obtén alivio rápido aplicando una pequeña cantidad de anestésico tópico (como Orabase u Ora-Gel) directamente sobre la zona con llagas con un hisopo de algodón.",
			"Puedes volver a aplicarlo según sea necesario.",
		],
	},
	{
		title: "Comida atrapada entre los dientes",
		paragraphs: [
			"Esto no es una emergencia, pero puede ser incómodo o vergonzoso. Se soluciona fácilmente con hilo dental.",
			"Prueba hacer un pequeño nudo en medio del hilo dental para ayudar a retirar la comida, o usa un cepillo interproximal o un palillo para desalojar comida atrapada entre tus dientes y brackets.",
		],
	},
	{
		title: "Labios o mejillas irritados",
		paragraphs: [
			"A veces los brackets nuevos pueden irritar la boca, especialmente al comer. Una pequeña cantidad de cera de alivio no medicada crea una excelente barrera entre el metal y tu boca.",
			"Simplemente toma un pedacito y haz una bolita del tamaño de un chícharo pequeño. Aplánala y colócala completamente sobre el área de los brackets que causa irritación.",
			"Luego podrás comer con más comodidad. Si accidentalmente tragas la cera, no hay problema. La cera es inofensiva.",
		],
	},
	{
		title: "Ligaduras que se salen",
		paragraphs: [
			"Las pequeñas ligas o alambres finos, conocidos como ligaduras, sostienen el alambre al bracket. Si se sale una ligadura de goma, quizá puedas volver a colocarla con pinzas estériles. Si una ligadura de alambre se afloja, retírala con pinzas estériles.",
			"Si la ligadura de alambre sobresale hacia el labio pero no está suelta, puedes doblarla hacia abajo con un hisopo o el borrador de un lápiz para que no irrite el labio. Por supuesto, cuando una ligadura se sale o se rompe, otras pueden seguir.",
			"Asegúrate de revisar todas las ligaduras. Las ligaduras faltantes o rotas deben notificarse a la Dra. Garcia-Paul. Si se pierde una ligadura de goma o alambre, avísale a la Dra. Garcia-Paul para que te indique si debes ser atendido.",
		],
	},
	{
		title: "Alambre que sobresale",
		paragraphs: [
			"Ocasionalmente, el extremo de un alambre puede salirse de lugar e irritar la boca. Usa un hisopo o el borrador de un lápiz para empujar el alambre hasta que quede plano contra el diente.",
			"Si el alambre no puede moverse a una posición cómoda, cúbrelo con cera de alivio. (Consulta las instrucciones anteriores en Labios o mejillas irritados para aplicar la cera).",
			"Deberás informar a la Dra. Garcia-Paul sobre el problema.",
			"Si el alambre causa muchas molestias y no puedes ver a la Dra. Garcia-Paul pronto, como último recurso puedes cortarlo.",
			"Reduce la posibilidad de tragar el pedazo de alambre cortado usando una gasa o pañuelo doblado alrededor del área.",
			"Usa un cortaúñas afilado para cortar el alambre que sobresale. Es posible que aún necesites cera de alivio para dar comodidad al área irritada.",
		],
	},
	{
		title: "Brackets, alambres o bandas sueltas",
		paragraphs: [
			"Si tus brackets se aflojan de cualquier manera, llama a la Dra. Garcia-Paul para determinar los próximos pasos.",
			"Los brackets son las partes de los aparatos que se adhieren a los dientes con un adhesivo especial. Generalmente se colocan en el centro de cada diente. El bracket puede despegarse si comiste alimentos duros o crujientes que se te indicó evitar, o si recibiste un golpe en la boca mientras jugabas.",
			"Animamos a todos los pacientes, especialmente a quienes usan brackets, a usar protector bucal al practicar deportes.",
			"Si el bracket está fuera de centro, el adhesivo puede haber fallado. En este caso, lo mejor es avisar de inmediato a la Dra. Garcia-Paul, quien determinará qué hacer.",
			"Si el bracket suelto giró sobre el alambre, sobresale y no puedes venir a la oficina de inmediato, puedes hacer un arreglo temporal para aliviar la molestia y evitar más daño, teniendo cuidado de no tragarlo ni causar otra lesión.",
			"Para volver a colocar el bracket en su lugar, usa pinzas estériles para deslizarlo sobre el alambre hasta que quede entre dos dientes.",
		],
	},
	{
		title: "Tragas una pieza de tu aparato",
		paragraphs: [
			"Esto es raro, pero cuando sucede puede ser alarmante. Mantén la calma.",
			"Si estás tosiendo demasiado o tienes dificultad para respirar, es posible que hayas inhalado la pieza hacia las vías respiratorias o los pulmones.",
			"Si puedes ver la pieza, puedes intentar retirarla con cuidado.",
			"NO lo intentes si podrías causar daño.",
			"Si corresponde según la situación, revisa tus brackets para detectar problemas causados por la pieza faltante, como algo suelto o irritación, y trátalos como se indicó arriba.",
			"Si no puedes ver la pieza y crees que pudo haber sido inhalada, llama a la Dra. Garcia-Paul de inmediato.",
		],
	},
];

const orthoEmergenciesLocalization = {
	[Locale.EN]: {
		title: "Orthodontic Emergencies",
		introHeading: "Orthodontic Emergency Care",
		introParagraphs: [
			"Our patients at Miami Orthodontic Specialists probably won’t experience a true orthodontic emergency.",
			"If it does happen to you, here's what you need to know.",
		],
		helpHeading: "Here to Help You",
		helpParagraphs: [
			"These orthodontic emergencies list from least severe to most severe.",
			"Dr. Garcia-Paul only needs to give attention to the most severe emergencies.",
		],
		items: englishItems,
	},
	[Locale.ES]: {
		title: "Emergencias de ortodoncia",
		introHeading: "Atención para emergencias de ortodoncia",
		introParagraphs: [
			"Nuestros pacientes de Miami Orthodontic Specialists probablemente no tendrán una verdadera emergencia de ortodoncia.",
			"Si te sucede, esto es lo que necesitas saber.",
		],
		helpHeading: "Estamos aquí para ayudarte",
		helpParagraphs: [
			"Estas emergencias de ortodoncia están ordenadas de menor a mayor gravedad.",
			"La Dra. Garcia-Paul solo necesita atender las emergencias más graves.",
		],
		items: spanishItems,
	},
} satisfies Record<Locale, OrthoEmergenciesLocalization>;

export function OrthoEmergenciesPage() {
	const getLocalization = useLocalization(orthoEmergenciesLocalization);

	return (
		<Flex direction="column">
			<HeroStyle2
				showFullImageOnMobile
				heroImageUrl="/miami-ortho-15.jpg"
				title={getLocalization("title")}
				backgroundPosition="center"
			/>
			<article className={styles.orthoCareArticle}>
				<Heading as="h2" className={styles.orthoCareMainHeading}>
					{getLocalization("introHeading")}
				</Heading>
				{getLocalization("introParagraphs").map((paragraph) => (
					<Text as="p" className={styles.orthoCareCopy} key={paragraph}>
						{paragraph}
					</Text>
				))}
				<section className={styles.orthoCareSection}>
					<Heading as="h2" className={styles.orthoCareMainHeading}>
						{getLocalization("helpHeading")}
					</Heading>
					{getLocalization("helpParagraphs").map((paragraph) => (
						<Text as="p" className={styles.orthoCareCopy} key={paragraph}>
							{paragraph}
						</Text>
					))}
					<AnimatedAccordion
						classNames={{
							root: styles.emergencyAccordion,
							item: styles.emergencyAccordionItem,
							header: styles.emergencyAccordionHeader,
							trigger: styles.emergencyAccordionTrigger,
							icon: styles.emergencyAccordionIcon,
							content: styles.emergencyAccordionContent,
							contentInner: styles.emergencyAccordionContentInner,
						}}
						items={getLocalization("items").map((item) => ({
							id: item.title,
							title: item.title,
							content: item.paragraphs.map((paragraph) => (
								<Text as="p" className={styles.orthoCareCopy} key={paragraph}>
									{paragraph}
								</Text>
							)),
						}))}
					/>
				</section>
			</article>
			<Box className={styles.formWrap}>
				<HomeConsultForm />
			</Box>
		</Flex>
	);
}
